import { computed, Injectable } from '@angular/core';
import { OpenAI } from '@maximizer/core/shared/domain';
import { Observable, map, of, tap } from 'rxjs';
import {
  AnonymizeResponseData,
  PIIContext,
} from '../classes/pii-anonymization.types';
import { PiiAnonymizationHelpers } from '../classes/pii-anonymization.helpers';
import { MaximizerAiService } from './maximizer-ai.service';
import { IQBoostViewStore } from '../state/iqboost-view.store';
import { IQBoostConversationMessages } from '../classes/iqBoost';

export type PiiDeAnonymizeLookup = (maskInner: string) => string | undefined;

@Injectable()
export class PiiAnonymizationService {

  private readonly _maskToOriginal = new Map<string, string>();

  get maskToOriginal(): ReadonlyMap<string, string> {
    return this._maskToOriginal;
  }

  anonymized = computed(() => 
    this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures.anonymized || 
    this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures.anonymized
  );

  constructor(
    private readonly cache: MaximizerAiService,
    private readonly store: IQBoostViewStore,
  ) {}

  clear(): void {
    this._maskToOriginal.clear();
  }

  deAnonymize(maskInner: string): string | undefined {
    return this._maskToOriginal.get(`<${maskInner.toUpperCase()}>`) ?? undefined;
  }

  deAnonymizeHtml(html: string): string {
    return PiiAnonymizationHelpers.processPiiMaskTokensInHtml(html, (type, number) => {
      const inner = `${type}${number}`;
      return this.deAnonymize(inner) ?? inner;
    });
  }

  anonymizeMessages(messages: OpenAI.GPTMessage[], piiContext?: PIIContext, clearMappings = true): Observable<OpenAI.GPTMessage[]> {
    if (!messages?.length) {
      return of(messages ?? []);
    }

    if (clearMappings) {
      this.clear();
    }
    return this.cache.anonymize(PiiAnonymizationHelpers.toAnonymizeRequest(messages, piiContext)).pipe(
      tap((response: AnonymizeResponseData) => {
        (response.Data.Entities ?? []).forEach((entity) =>
          this._maskToOriginal.set(`<${entity.Type.toUpperCase()}>`, entity.Value),
        );
      }),
      map(response => PiiAnonymizationHelpers.toOpenAiMessages(messages, response.Data.Texts)),
      map((out) => (out.length > 0 ? out : messages)),
    );
  }

  anonymizeByIdentities(text: string, identities: ReadonlyMap<string, string>): string {
    if (!identities?.size) {
      return text;
    }

    let result = text;
    for (const [mask, original] of identities.entries()) {
      if (!original) continue;
      result = result.replaceAll(PiiAnonymizationHelpers.wholeWordPattern(original), mask);
    }
    return result;
  }

  anonymizeContextAndUser(
    messages: IQBoostConversationMessages,
    piiContext?: PIIContext,
  ): Observable<IQBoostConversationMessages> {
    const { context: contextMessages, user: userMessages, system: systemMessages } = messages;

    if (!this.anonymized()) {
      return of(messages);
    }

    this.clear();
    const combined = [...contextMessages, ...userMessages, ...systemMessages];
    return this.anonymizeMessages(combined, piiContext, false)
      .pipe(
        map((anonymizedMessages) =>
          PiiAnonymizationHelpers.splitConversationMessages(
            anonymizedMessages,
            contextMessages.length,
            userMessages.length,
          ),
        ),
      );
  }

  static processPiiMaskTokensInHtml(
    htmlText: string,
    transformFn: (type: string, number: string, match: string) => string,
  ): string {
    return PiiAnonymizationHelpers.processPiiMaskTokensInHtml(htmlText, transformFn);
  }
}
